// src/components/AudioPlayerOverlay.jsx
import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Detect URL type
function getUrlType(url) {
  if (!url) return null;
  // Firebase Storage — must check before generic googleapis.com
  if (url.includes("firebasestorage.googleapis.com") ||
      url.includes("storage.googleapis.com")) return "firebase";
  // Google Drive
  if (url.includes("drive.google.com") ||
      (url.includes("googleapis.com") && url.includes("export=download"))) return "drive";
  // Any other http/https URL — try native player
  if (url.startsWith("http://") || url.startsWith("https://")) return "direct";
  return null;
}

// Resolve the best playable URL from a track object
function resolveUrl(track) {
  return track?.url || track?.fileUrl || track?.audioUrl || track?.downloadUrl || null;
}

// Extract Drive file ID and return preview URL
function drivePreviewUrl(url) {
  const m = url.match(/[-\w]{25,}/);
  if (!m) return null;
  return `https://drive.google.com/file/d/${m[0]}/preview`;
}

// Matches the Android app's practice speeds. Weighted slow because the
// main reason a student changes speed is to learn a difficult phrase.
const SPEEDS = [0.6, 0.8, 1, 1.25, 1.5];

export default function AudioPlayerOverlay({ track, onClose }) {
  const audioRef = useRef(null);
  const resolvedUrl = resolveUrl(track);
  const urlType     = getUrlType(resolvedUrl);
  const { t }       = useTranslation();
  const [speed, setSpeed] = useState(1);
  const [loop,  setLoop]  = useState(false);
  // Loading state for the native player. Without it the controls render
  // immediately over a zero-length track while the file is still being
  // fetched, so a student can't tell whether it's buffering or stuck —
  // especially on a slow connection with a several-MB recording.
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioError,   setAudioError]   = useState(false);

  // Auto-play for native audio
  useEffect(() => {
    if (audioRef.current && (urlType === "firebase" || urlType === "direct")) {
      setAudioLoading(true);
      setAudioError(false);
      audioRef.current.load();
      audioRef.current.play().catch(() => {});
    }
  }, [resolvedUrl, urlType]);

  // Apply playback speed whenever it changes
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  // Apply loop setting whenever it changes
  useEffect(() => {
    if (audioRef.current) audioRef.current.loop = loop;
  }, [loop]);

  // Reset speed + loop when a new track opens
  useEffect(() => { setSpeed(1); setLoop(false); }, [resolvedUrl]);

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!track) return null;

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position:"fixed", inset:0,
        background:"rgba(0,0,0,.6)",
        zIndex:999,
        display:"flex",
        alignItems:"flex-end",
        justifyContent:"center",
      }}>

      <div className="audio-player-sheet" style={{
        width:"100%",
        maxWidth:"600px",
        background:"linear-gradient(135deg,#4E0808,#7B1515)",
        borderRadius:"20px 20px 0 0",
        padding:"20px 20px 32px",
        boxShadow:"0 -8px 40px rgba(0,0,0,.4)",
        animation:"slideUp .3s ease",
      }}>

        {/* Drag handle */}
        <div style={{
          width:"40px", height:"4px",
          background:"rgba(255,255,255,.3)",
          borderRadius:"99px",
          margin:"0 auto 16px",
        }}/>

        {/* Title row */}
        <div style={{ display:"flex", alignItems:"center",
                      justifyContent:"space-between", marginBottom:"16px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"12px", minWidth:0 }}>
            <div style={{
              width:"44px", height:"44px", borderRadius:"10px",
              background:"rgba(255,255,255,.15)",
              display:"flex", alignItems:"center",
              justifyContent:"center", fontSize:"1.4rem", flexShrink:0,
            }}>🎵</div>
            <div style={{ minWidth:0 }}>
              <div style={{
                color:"white", fontWeight:"700", fontSize:".95rem",
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              }}>{track.title}</div>
              {track.description && (
                <div style={{
                  color:"rgba(255,255,255,.6)", fontSize:".78rem", marginTop:"2px",
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                }}>{track.description}</div>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{
            background:"rgba(255,255,255,.15)", border:"none",
            color:"white", borderRadius:"50%",
            width:"32px", height:"32px",
            cursor:"pointer", fontSize:".9rem",
            display:"flex", alignItems:"center", justifyContent:"center",
            flexShrink:0, marginLeft:"12px",
          }}>✕</button>
        </div>

        {/* Player */}
        {(urlType === "firebase" || urlType === "direct") ? (
          <>
            <div style={{ position:"relative" }}>
              <audio
                ref={audioRef}
                controls
                controlsList="nodownload"
                onContextMenu={e => e.preventDefault()}
                onLoadStart={() => { setAudioLoading(true); setAudioError(false); }}
                onCanPlay={()   => setAudioLoading(false)}
                onPlaying={()   => { setAudioLoading(false); setAudioError(false); }}
                onWaiting={()   => setAudioLoading(true)}
                onError={()     => { setAudioLoading(false); setAudioError(true); }}
                style={{
                  width:"100%", borderRadius:"12px", outline:"none",
                  opacity: audioLoading || audioError ? 0.25 : 1,
                  transition:"opacity .2s ease",
                }}
              >
                <source src={resolvedUrl} type="audio/mpeg"/>
                <source src={resolvedUrl}/>
              </audio>

              {(audioLoading || audioError) && (
                <div style={{
                  position:"absolute", inset:0,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  gap:"10px", pointerEvents:"none",
                }}>
                  {audioError ? (
                    <span style={{ color:"#fca5a5", fontSize:".82rem", fontWeight:600, textAlign:"center" }}>
                      Could not load this track. Check your connection.
                    </span>
                  ) : (
                    <>
                      <span style={{
                        width:"18px", height:"18px", flexShrink:0,
                        border:"2px solid rgba(255,255,255,.25)",
                        borderTopColor:"#fff", borderRadius:"50%",
                        display:"inline-block",
                        animation:"ssSpin .8s linear infinite",
                      }}/>
                      <span style={{ color:"rgba(255,255,255,.75)", fontSize:".82rem", fontWeight:600 }}>
                        Loading track…
                      </span>
                    </>
                  )}
                </div>
              )}
              <style>{`@keyframes ssSpin { to { transform: rotate(360deg); } }`}</style>
            </div>

            {/* Speed + Loop controls */}
            <div style={{
              display:"flex", alignItems:"center",
              justifyContent:"space-between",
              gap:"8px", marginTop:"12px",
            }}>
              {/* Speed — nowrap so five options stay on one line rather
                  than reflowing, which reads as a layout accident. */}
              <div style={{ display:"flex", alignItems:"center", gap:"5px", flexWrap:"nowrap" }}>
                <span style={{
                  color:"rgba(255,255,255,.5)", fontSize:".72rem",
                  fontWeight:"600", letterSpacing:".5px", flexShrink:0,
                }}>SPEED</span>
                {SPEEDS.map(s => (
                  <button key={s} onClick={() => setSpeed(s)} style={{
                    padding:"4px 11px",
                    borderRadius:"99px",
                    border: speed === s
                      ? "1.5px solid white"
                      : "1.5px solid rgba(255,255,255,.25)",
                    background: speed === s ? "white" : "transparent",
                    color:      speed === s ? "#7B1515" : "rgba(255,255,255,.7)",
                    fontWeight:"700",
                    fontSize:".78rem",
                    cursor:"pointer",
                    transition:"all .15s",
                    lineHeight:1.4,
                  }}>
                    {s === 1 ? "1×" : `${s}×`}
                  </button>
                ))}
              </div>

              {/* Loop */}
              <button onClick={() => setLoop(l => !l)} title={loop ? "Loop on" : "Loop off"} style={{
                display:"flex", alignItems:"center", gap:"4px",
                padding:"4px 11px",
                borderRadius:"99px",
                border: loop ? "1.5px solid white" : "1.5px solid rgba(255,255,255,.25)",
                background: loop ? "white" : "transparent",
                color:      loop ? "#7B1515" : "rgba(255,255,255,.7)",
                fontWeight:"700",
                fontSize:".78rem",
                cursor:"pointer",
                transition:"all .15s",
                lineHeight:1.4,
                flexShrink:0,
              }}>
                🔁
              </button>
            </div>
          </>
        ) : urlType === "drive" ? (
          /* Legacy Drive files — try native first, iframe as fallback */
          <>
            <audio
              ref={audioRef}
              controls
              controlsList="nodownload"
              style={{ width:"100%", borderRadius:"12px", outline:"none", marginBottom:"8px" }}
            >
              <source src={resolvedUrl}/>
            </audio>
            <iframe
              src={drivePreviewUrl(resolvedUrl)}
              title={track.title}
              allow="autoplay"
              style={{
                width:"100%", height:"80px",
                border:"none", borderRadius:"12px",
                background:"rgba(255,255,255,.08)",
              }}
            />
          </>
        ) : (
          <div style={{ color:"rgba(255,255,255,.5)", textAlign:"center",
                        fontSize:".85rem", padding:"16px 0" }}>
            {t("player.unableToLoad")}
          </div>
        )}

        <div style={{
          textAlign:"center", marginTop:"10px",
          color:"rgba(255,255,255,.35)", fontSize:".7rem",
        }}>
          {t("player.brand")}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @media (max-width: 900px) {
          .audio-player-sheet {
            margin-bottom: 62px !important;
            border-radius: 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
